/**
 * @module app.pp
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { focusFirst } from 'app/shared/lib/focus';
import { validate } from 'app/shared/elements/ess-rule';
import * as UM from 'app/um/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { AuthContext } from 'app/um/lib/auth-context';
import { UploadDocument } from 'app/core/components/documents/upload-document';
import { DocumentCategorySelectedEvent, DocumentFileUploadedEvent } from 'app/core/lib/events';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';


@autoinject()
export class DocumentationStep extends ComponentBase implements IWizardStep {

    ////****************** Fields and Properties ************************************////

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected auth: UM.Auth = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected missingRequiredData: string[] = [];

    public canSkip: boolean = false;
    public canContinue: boolean = true;
    public canPerformFinalSubmit: boolean = false;

    protected addDocSelection: string = 'no';
    protected fileUploaded: boolean = false;
    protected selectedCategory: Core.DocumentCategory | null = null;

    protected validate = validate;
    protected $documentIsValid: boolean = false;

    private subscriptions: Array<Subscription> = [];

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        categoryFlagCodes: ['PP.View'],
        addTargetProperty: (document: UM.AuthDocument) => { document.auth = { id: this.auth?.id } }
    };

    ////****************** Element References ***************************************////

    protected addDocument!: UploadDocument;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private $element: Element,
        private readonly context: AuthContext,
        private readonly events: EventAggregator,

        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;
    }

    protected bind(): void {
        this.subscriptions.push(
            this.events.subscribe(DocumentCategorySelectedEvent, (e: DocumentCategorySelectedEvent) => this.handleDocumentCategorySelectedEvent(e))
        );

        this.subscriptions.push(
            this.events.subscribe(DocumentFileUploadedEvent, (e: DocumentFileUploadedEvent) => this.handleDocumentFileUploadedEvent())
        );
    }

    ////****************** Functions ************************************************////

    public skipStep(): void { }

    public continueStep(): void {
        if(this.addDocSelection == 'yes' && this.addDocument) {
            const document = this.addDocument.buildDocument();
            this.context.auth.documents = [document];
        }
    }

    public openStep(): void {
        setTimeout(() => focusFirst(this.$element), 250);
    }

    public async closeStep(): Promise<void> { }

    public async finalSubmit(): Promise<void> { }

    protected handleAddDocumentToggle(): void {
        if(this.addDocSelection == 'yes') {
            if(!this.selectedCategory && !this.missingRequiredData.some(s => s == 'Document Category')) this.missingRequiredData.push('Document Category');
            if(!this.fileUploaded && !this.missingRequiredData.some(s => s == 'File')) this.missingRequiredData.push('File');
            if(this.selectedCategory) this.recalculateMissingData('Document Category');
            if(this.fileUploaded) this.recalculateMissingData('File');
            this.canContinue = this.missingRequiredData.length === 0;
        }
        else {
            this.recalculateMissingData('Document Category');
            this.recalculateMissingData('File');
        }
    }

    private handleDocumentCategorySelectedEvent(e: DocumentCategorySelectedEvent): void {
        if(e.category) this.selectedCategory = e.category;
        else this.selectedCategory = null;
        this.handleAddDocumentToggle();
    }

    private handleDocumentFileUploadedEvent(): void {
        this.fileUploaded = true;
        this.handleAddDocumentToggle();
    }

    private recalculateMissingData(fieldName: string): void {
        this.missingRequiredData.splice(this.missingRequiredData.findIndex(s => s == fieldName), 1);
        this.canContinue = this.missingRequiredData.length === 0;
    }

}
